import { isHeartbeatSchedulerToolName } from "./toolDisplayName";
import type { IDAChatMessage } from "./chatMessageMapper";

const HEARTBEATS_CHANGED_EVENT = "ida:heartbeats-changed";
const heartbeatEventTarget = new EventTarget();

export interface HeartbeatsChangedDetail {
  toolName?: string;
  reason?: "tool-completed";
}

export interface CompletedHeartbeatExecution {
  key: string;
  detail: HeartbeatsChangedDetail;
}

export function emitHeartbeatsChanged(detail?: HeartbeatsChangedDetail): void {
  heartbeatEventTarget.dispatchEvent(
    new CustomEvent<HeartbeatsChangedDetail>(HEARTBEATS_CHANGED_EVENT, {
      detail,
    }),
  );
}

export function maybeEmitHeartbeatsChangedForTool(name?: string): boolean {
  if (!isHeartbeatSchedulerToolName(name)) {
    return false;
  }

  emitHeartbeatsChanged({
    toolName: name,
    reason: "tool-completed",
  });
  return true;
}

export function collectCompletedHeartbeatExecutions(
  messages: Pick<IDAChatMessage, "id" | "toolExecutions">[],
): CompletedHeartbeatExecution[] {
  const executions = new Map<string, CompletedHeartbeatExecution>();

  for (const message of messages) {
    for (const execution of message.toolExecutions || []) {
      if (execution.status !== "completed") continue;
      if (!isHeartbeatSchedulerToolName(execution.name)) continue;

      const key = `${message.id}:${execution.id}`;
      executions.set(key, {
        key,
        detail: {
          toolName: execution.name,
          reason: "tool-completed",
        },
      });
    }
  }

  return Array.from(executions.values());
}

export function addHeartbeatsChangedListener(
  listener: (detail?: HeartbeatsChangedDetail) => void,
): () => void {
  const handler = (event: Event) => {
    listener((event as CustomEvent<HeartbeatsChangedDetail>).detail);
  };

  heartbeatEventTarget.addEventListener(HEARTBEATS_CHANGED_EVENT, handler);
  return () => {
    heartbeatEventTarget.removeEventListener(HEARTBEATS_CHANGED_EVENT, handler);
  };
}
